/**
 * The three parameters Prolific puts on the study link, read off the page's
 * query string. The rule itself is shared with the server (`checkStudyParams`).
 *
 * Read before anything else on `/study`: a link with a missing or malformed
 * parameter gets "this link is incomplete" at once, rather than a microphone
 * prompt for a session that cannot be recorded against anybody.
 */

import type { StudyParamsVerdict } from '@overcooked/shared'
import { checkStudyParams, readStudyQuery } from '@overcooked/shared'

export function parseStudyParams(search: string, allowTestPids: boolean): StudyParamsVerdict {
  const raw = readStudyQuery(search)
  return checkStudyParams(
    { prolificPid: raw.prolificPid ?? '', studyId: raw.studyId ?? '', sessionId: raw.sessionId ?? '' },
    allowTestPids,
  )
}

/**
 * Where "the tech check passed in this tab" is remembered, so a refresh goes
 * straight back to the flow. `sessionStorage`: it belongs to the tab.
 */
export const techPassedKey = (pid: string): string => `overcooked.study.tech.${pid}`

type SessionStore = Pick<Storage, 'getItem' | 'setItem'>

function tabStore(): SessionStore | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export function techAlreadyPassed(pid: string, store: SessionStore | null = tabStore()): boolean {
  try {
    return store?.getItem(techPassedKey(pid)) === '1'
  } catch {
    return false
  }
}

export function rememberTechPassed(pid: string, store: SessionStore | null = tabStore()): void {
  try {
    store?.setItem(techPassedKey(pid), '1')
  } catch {
    // A tab that refuses storage runs the check again after a refresh.
  }
}

/**
 * Where the tab remembers which tutorial card it was on, so a refresh resumes
 * there instead of at card 1. Per participant, in `sessionStorage`.
 */
export const tutorialStepKey = (pid: string): string => `overcooked.study.tutorial.${pid}`

/** The card to resume at: 0-based, within `0..steps-1`, and 0 for anything unreadable. */
export function tutorialStepFor(pid: string, steps: number, store: SessionStore | null = tabStore()): number {
  try {
    const raw = store?.getItem(tutorialStepKey(pid))
    if (!raw || !/^\d{1,4}$/.test(raw)) return 0
    return Math.max(0, Math.min(Number(raw), Math.max(0, steps - 1)))
  } catch {
    return 0
  }
}

export function rememberTutorialStep(pid: string, index: number, store: SessionStore | null = tabStore()): void {
  try {
    if (Number.isInteger(index) && index >= 0) store?.setItem(tutorialStepKey(pid), String(index))
  } catch {
    // A tab that refuses storage starts the tutorial over after a refresh.
  }
}
